import React, { useState, useEffect, useMemo } from 'react'
import {
  FileText,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  AlertCircle,
  Search,
  X,
  Zap,
  Calendar
} from 'lucide-react'
import { motion } from 'framer-motion'
import Tooltip from '../ui/Tooltip'
import Card from '../ui/Card'
import GenerateReportDrawer, {
  ReportGenerationConfig
} from './GenerateReportDrawer'
import ScheduleReportDrawer, {
  ReportScheduleConfig
} from './ScheduleReportDrawer'
import ScheduledReportsTab from './ScheduledReportsTab'
import { useToast } from '../../contexts/ToastContext'
import {
  useNavigationTracking,
  useReportTracking
} from '../../contexts/AnalyticsContext'
import useReportStore from '../../stores/reportStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import SharedReportsTab from './SharedReportsTab'
import ShareReportBubble from './ShareReportBubble'
import SaveShareDrawer from './SaveShareDrawer'
import ReportViewer from './ReportViewer'
import { SharedReportListItem } from '../../types/sharedReport'
import { sharedReportService } from '../../services/sharedReportService'
import { ScheduledReport, ReportFormat, ReportType } from '../../types/report'
import { reportService } from '../../services/reportService'

interface ReportCard {
  id: string
  title: string
  description: string
  format: 'pdf' | 'csv' | 'both'
  category: string
}

// Convert category slug to display name (e.g. 'data-export' → 'Data Export')
const getCategoryDisplayName = (category: string): string => {
  return category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Transform ReportType from API to ReportCard for UI
const transformReportTypeToCard = (reportType: ReportType): ReportCard => {
  const format =
    reportType.metadata.format === 'both'
      ? 'both'
      : (reportType.metadata.format as 'pdf' | 'csv')

  return {
    id: reportType.typeKey,
    title: reportType.metadata.displayName,
    description: reportType.metadata.description,
    format,
    category: reportType.metadata.category,
  }
}

const ReportsPage: React.FC = () => {
  const { showSuccess, showError, showInfo } = useToast()
  const { trackTabChanged } = useNavigationTracking()
  const {
    trackReportGenerated,
    trackReportGenerationFailed,
    trackScheduleCreated,
    trackScheduleUpdated
  } = useReportTracking()
  const {
    createScheduledReport,
    updateScheduledReport,
    reportTypes,
    reportTypesLoading,
    reportTypesError,
    loadReportTypes
  } = useReportStore()
  const { selectedCustomer } = useGlobalCustomerStore()
  const [activeTab, setActiveTab] = useState<
    'available' | 'shared' | 'scheduled'
  >('available')
  const [isGenerateDrawerOpen, setIsGenerateDrawerOpen] = useState(false)
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<ReportCard | null>(null)
  const [editingSchedule, setEditingSchedule] =
    useState<ScheduledReport | null>(null)
  const [reportSearchTerm, setReportSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  // Get customer ID from global store
  const customerId = selectedCustomer?.id || null

  // Shareable Reports: Save & Share drawer + in-app viewer state
  const [shareOpen, setShareOpen] = useState(false)
  const [shareItem, setShareItem] = useState<SharedReportListItem | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerItem, setViewerItem] = useState<SharedReportListItem | null>(null)
  const [sharedReloadSignal, setSharedReloadSignal] = useState(0)

  // Load report types on mount
  useEffect(() => {
    loadReportTypes()
  }, [loadReportTypes])

  // Transform API report types to report cards
  const reportCards: ReportCard[] = reportTypes
    .filter((rt) => rt.isActive)
    .map(transformReportTypeToCard)

  // Derive unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = Array.from(new Set(reportCards.map(r => r.category || 'other')))
    return cats
      .sort((a, b) => getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b)))
      .map(cat => ({ value: cat, label: getCategoryDisplayName(cat) }))
  }, [reportCards])

  // Filter by search term and category
  const filteredReports = useMemo(() => {
    let filtered = reportCards

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => (r.category || 'other') === selectedCategory)
    }

    if (reportSearchTerm.trim()) {
      const search = reportSearchTerm.toLowerCase()
      filtered = filtered.filter(report =>
        report.title.toLowerCase().includes(search) ||
        report.description.toLowerCase().includes(search)
      )
    }

    return filtered.sort((a, b) => a.title.localeCompare(b.title))
  }, [reportCards, reportSearchTerm, selectedCategory])

  const handleGenerateClick = (report: ReportCard) => {
    setSelectedReport(report)
    setIsGenerateDrawerOpen(true)
  }

  const handleScheduleClick = (report: ReportCard) => {
    setSelectedReport(report)
    setIsScheduleDrawerOpen(true)
  }

  const handleGenerateReport = async (config: ReportGenerationConfig) => {
    if (!selectedReport) return

    try {
      // Determine format based on report type
      const format: ReportFormat =
        selectedReport.format === 'csv' ? 'csv' : 'pdf'

      if (format === 'pdf') {
        // For PDF reports, open in new tab
        await generatePdfReportInNewTab(config)
        showSuccess('Report opened in new tab')
        trackReportGenerated(config.reportType, 'pdf', config.customerId)
      } else {
        // For CSV reports, auto-download using reportService
        await reportService.generateReport({
          ...config,
          format: 'csv'
        })
        showSuccess('CSV export downloaded')
        trackReportGenerated(config.reportType, 'csv', config.customerId)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      trackReportGenerationFailed(
        config.reportType,
        errorMessage,
        config.customerId
      )
      showError(
        'Failed to generate report',
        error instanceof Error ? error.message : 'Please try again later.'
      )
      throw error // Re-throw to let drawer handle loading state
    }
  }

  const generatePdfReportInNewTab = async (config: ReportGenerationConfig) => {
    const url = `${
      import.meta.env.VITE_REPORT_API_URL || 'http://localhost:3006/api/v1'
    }/reports/generate`
    const token = localStorage.getItem('auth-token')
    // Report generation is synchronous server-side (data query + Puppeteer setContent 60s +
    // page.pdf 90s = ~150s worst case), so the response headers don't arrive until the whole
    // PDF is built. 30s was too short for larger reports and surfaced as a "connection" error.
    // 180s stays above the backend's max and below Azure's ~230s idle cap, so the backend's own
    // result always surfaces before the client aborts.
    const timeoutMs = 180000

    const requestConfig: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({
        ...config,
        format: 'pdf'
      })
    }

    // Create timeout promise for initial request
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, requestConfig), timeoutPromise])

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message ||
          errorData.error?.message ||
          `HTTP ${response.status}`
      )
    }

    // Get the PDF blob with timeout
    const blobTimeoutMs = 120000 // allow the full generation window for the PDF body
    const blobTimeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`PDF download timeout after ${blobTimeoutMs / 1000}s`))
      }, blobTimeoutMs)
    })

    const blob = await Promise.race([response.blob(), blobTimeoutPromise])

    // Validate blob
    if (!blob || blob.size === 0) {
      throw new Error('Downloaded PDF is empty or invalid')
    }

    // Create a blob URL and open in new tab
    const blobUrl = window.URL.createObjectURL(blob)

    // Open in new tab
    const newWindow = window.open(blobUrl, '_blank')

    if (!newWindow) {
      // Popup blocked — the URL is unused, so free it immediately.
      window.URL.revokeObjectURL(blobUrl)
      throw new Error('Popup blocked. Please allow popups for this site.')
    }
    // Do NOT revoke on a timer. The opened tab holds this blob: URL for its whole lifetime,
    // and the browser's PDF-viewer Download button re-references it — revoking early (we used
    // to after 5s) made a later "Download" fail with a network / "check internet connection"
    // error. The browser reclaims the URL automatically when this page unloads, so the tiny
    // memory cost is fine.
  }

  const handleScheduleReport = async (config: ReportScheduleConfig) => {
    if (!selectedReport) return

    // Map scheduleType to ScheduleFrequency
    const scheduleFrequency =
      config.scheduleType === 'one-time'
        ? 'one-time'
        : config.recurringFrequency || 'weekly'

    try {
      await createScheduledReport({
        reportType: selectedReport.id,
        reportName: config.reportName,
        customerId: config.customerId,
        parameters: {
          period: config.period,
          customStartDate: config.customStartDate,
          customEndDate: config.customEndDate,
          accountIds: config.accountIds,
          postTypes: config.postTypes,
          ...config.dynamicParams,
        },
        schedule: {
          type: scheduleFrequency,
          recurringDay: config.recurringDay,
          recurringTime: config.recurringTime,
          oneTimeDate: config.oneTimeDate,
          oneTimeTime: config.oneTimeTime,
          timezone: config.timezone
        },
        emailConfig: {
          enabled: config.emailReport,
          recipients: [],
          recipientRefs: config.recipientRefs || []
        }
      })
      trackScheduleCreated(
        selectedReport.id,
        config.scheduleType === 'one-time' ? 'one-time' : 'recurring',
        config.recurringFrequency,
        config.emailReport,
        config.customerId
      )
      showSuccess('Report scheduled successfully')
    } catch (error) {
      showError(
        'Failed to schedule report',
        error instanceof Error ? error.message : 'Please try again later.'
      )
      throw error // Re-throw to let drawer handle loading state
    }
  }

  const handleUpdateSchedule = async (
    scheduleId: string,
    config: ReportScheduleConfig
  ) => {
    showInfo('Updating schedule...', 'Saving your changes.')

    // Map scheduleType to ScheduleFrequency
    const scheduleFrequency =
      config.scheduleType === 'one-time'
        ? 'one-time'
        : config.recurringFrequency || 'weekly'

    try {
      await updateScheduledReport(scheduleId, {
        reportName: config.reportName,
        parameters: {
          period: config.period,
          customStartDate: config.customStartDate,
          customEndDate: config.customEndDate,
          accountIds: config.accountIds,
          postTypes: config.postTypes,
          ...config.dynamicParams,
        },
        schedule: {
          type: scheduleFrequency,
          recurringDay: config.recurringDay,
          recurringTime: config.recurringTime,
          oneTimeDate: config.oneTimeDate,
          oneTimeTime: config.oneTimeTime,
          timezone: config.timezone
        },
        emailConfig: {
          enabled: config.emailReport,
          recipients: [],
          recipientRefs: config.recipientRefs || []
        }
      })
      trackScheduleUpdated(
        scheduleId,
        editingSchedule?.reportType || '',
        config.customerId
      )
      showSuccess('Schedule updated successfully')
      setEditingSchedule(null)
    } catch (error) {
      showError(
        'Failed to update schedule',
        error instanceof Error ? error.message : 'Please try again later.'
      )
      throw error
    }
  }

  const handleEditSchedule = (schedule: ScheduledReport) => {
    setEditingSchedule(schedule)
    setIsScheduleDrawerOpen(true)
  }

  const handleCloseScheduleDrawer = () => {
    setIsScheduleDrawerOpen(false)
    setEditingSchedule(null)
  }

  const getFormatBadge = (format: 'pdf' | 'csv' | 'both') => {
    if (format === 'both') {
      return (
        <div className='flex items-center space-x-1'>
          <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-200'>
            PDF
          </span>
          <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200'>
            CSV
          </span>
        </div>
      )
    }

    return format === 'pdf' ? (
      <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-200'>
        PDF
      </span>
    ) : (
      <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200'>
        CSV
      </span>
    )
  }

  return (
    <div className='min-h-screen bg-gray-50/50'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Header */}
        <div className='mb-6'>
          <h1 className='text-2xl font-bold text-gray-900 tracking-tight'>
            Reports
          </h1>
          <p className='text-sm text-gray-600 mt-1'>
            Generate, schedule, and download analytics reports
          </p>
        </div>

        {/* Tab Navigation */}
        <div className='flex space-x-1 mb-6 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner'>
          <button
            onClick={() => {
              trackTabChanged('available', activeTab)
              setActiveTab('available')
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === 'available'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            Available Reports
          </button>
          <button
            onClick={() => {
              trackTabChanged('shared', activeTab)
              setActiveTab('shared')
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === 'shared'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            Shared Reports
          </button>
          <button
            onClick={() => {
              trackTabChanged('scheduled', activeTab)
              setActiveTab('scheduled')
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === 'scheduled'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            Scheduled Reports
          </button>
        </div>

        {/* One-time "how to share a report" bubble */}
        {activeTab === 'available' && <ShareReportBubble />}

        {/* Content Area */}
        {activeTab === 'available' && (
          <>
            {/* Loading State */}
            {reportTypesLoading && (
              <div className='flex items-center justify-center py-12'>
                <div className='flex items-center space-x-3'>
                  <Loader2 className='h-6 w-6 text-blue-600 animate-spin' />
                  <span className='text-gray-600'>
                    Loading available reports...
                  </span>
                </div>
              </div>
            )}

            {/* Error State */}
            {reportTypesError && !reportTypesLoading && (
              <div className='bg-red-50 border border-red-200 rounded-lg p-6 text-center'>
                <AlertCircle className='h-10 w-10 text-red-500 mx-auto mb-3' />
                <h3 className='text-lg font-medium text-red-800 mb-2'>
                  Failed to load report types
                </h3>
                <p className='text-sm text-red-600 mb-4'>{reportTypesError}</p>
                <button
                  onClick={() => loadReportTypes()}
                  className='inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700'
                >
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Try Again
                </button>
              </div>
            )}

            {/* Empty State */}
            {!reportTypesLoading &&
              !reportTypesError &&
              reportCards.length === 0 && (
                <div className='bg-gray-50 border border-gray-200 rounded-lg p-12 text-center'>
                  <FileText className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                  <h3 className='text-lg font-medium text-gray-900 mb-2'>
                    No report types available
                  </h3>
                  <p className='text-sm text-gray-600'>
                    Report types will appear here once configured.
                  </p>
                </div>
              )}

            {/* Available Reports Table */}
            {!reportTypesLoading &&
              !reportTypesError &&
              reportCards.length > 0 && (
                <div className='space-y-4'>
                  {/* Search + Category Filter Bar */}
                  <Card padding='none' className='p-4'>
                    <div className='flex items-center justify-between gap-4'>
                      <div className='flex items-center gap-3 flex-1'>
                        <div className='relative flex-1 max-w-sm'>
                          <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
                          <input
                            type='text'
                            placeholder='Search reports...'
                            value={reportSearchTerm}
                            onChange={(e) => setReportSearchTerm(e.target.value)}
                            className='w-full pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                          />
                          {reportSearchTerm && (
                            <button
                              onClick={() => setReportSearchTerm('')}
                              className='absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors'
                              aria-label='Clear search'
                            >
                              <X className='h-3.5 w-3.5' />
                            </button>
                          )}
                        </div>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className='py-1.5 px-2.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white hover:bg-gray-50 transition-colors'
                        >
                          <option value='all'>All Categories</option>
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <span className='text-xs text-gray-500'>
                        {filteredReports.length} of {reportCards.length} {reportCards.length === 1 ? 'report' : 'reports'}
                      </span>
                    </div>
                  </Card>

                  {/* No Results */}
                  {filteredReports.length === 0 && (
                    <Card padding='md'>
                      <div className='text-center py-8'>
                        <Search className='h-10 w-10 text-gray-300 mx-auto mb-3' />
                        <p className='text-sm text-gray-500'>
                          No reports match your filters
                        </p>
                        <button
                          onClick={() => { setReportSearchTerm(''); setSelectedCategory('all') }}
                          className='mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium'
                        >
                          Clear filters
                        </button>
                      </div>
                    </Card>
                  )}

                  {/* Reports Table */}
                  {filteredReports.length > 0 && (
                    <Card padding='none' className='overflow-hidden'>
                      <div className='overflow-x-auto'>
                        <table className='w-full'>
                          <thead className='bg-gray-50 border-b border-gray-200'>
                            <tr>
                              <th className='px-6 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                                Report Name
                              </th>
                              <th className='px-6 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                                Category
                              </th>
                              <th className='px-6 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                                Format
                              </th>
                              <th className='px-6 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell'>
                                Description
                              </th>
                              <th className='px-6 py-3 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider'>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {filteredReports.map((report) => (
                              <motion.tr
                                key={report.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className='hover:bg-blue-50 transition-colors duration-150'
                              >
                                <td className='px-6 py-3 whitespace-nowrap'>
                                  <div className='flex items-center'>
                                    <div className='flex-shrink-0 mr-3 text-gray-400'>
                                      {report.format === 'csv' ? (
                                        <FileSpreadsheet className='h-4 w-4' />
                                      ) : (
                                        <FileText className='h-4 w-4' />
                                      )}
                                    </div>
                                    <span className='text-xs font-medium text-gray-900'>
                                      {report.title}
                                    </span>
                                  </div>
                                </td>
                                <td className='px-6 py-3 whitespace-nowrap'>
                                  <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700'>
                                    {getCategoryDisplayName(report.category)}
                                  </span>
                                </td>
                                <td className='px-6 py-3 whitespace-nowrap'>
                                  {getFormatBadge(report.format)}
                                </td>
                                <td className='px-6 py-3 hidden md:table-cell'>
                                  <p className='text-xs text-gray-500'>
                                    {report.description}
                                  </p>
                                </td>
                                <td className='px-6 py-3 whitespace-nowrap text-right'>
                                  <div className='flex items-center justify-end space-x-2'>
                                    <Tooltip content='Generate report now' placement='top'>
                                      <button
                                        onClick={() => handleGenerateClick(report)}
                                        className='inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md'
                                      >
                                        <Zap className='h-3.5 w-3.5 mr-1' />
                                        Generate
                                      </button>
                                    </Tooltip>
                                    <Tooltip content='Schedule for later' placement='top'>
                                      <button
                                        onClick={() => handleScheduleClick(report)}
                                        className='inline-flex items-center justify-center p-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200'
                                      >
                                        <Calendar className='h-3.5 w-3.5' />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </div>
              )}
          </>
        )}

        {activeTab === 'shared' && (
          <SharedReportsTab
            customerId={customerId}
            reloadSignal={sharedReloadSignal}
            onView={(it) => { setViewerItem(it); setViewerOpen(true) }}
            onShare={(it) => { setShareItem(it); setShareOpen(true) }}
          />
        )}

        {activeTab === 'scheduled' && (
          <ScheduledReportsTab
            customerId={customerId}
            onEditSchedule={handleEditSchedule}
          />
        )}
      </div>

      {/* Shareable Reports: Save & Share drawer + in-app viewer */}
      <SaveShareDrawer
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        item={shareItem}
        onPreview={() => { setViewerItem(shareItem); setViewerOpen(true) }}
        onSubmit={async (payload) => {
          if (!shareItem?.snapshotId || !customerId) return
          try {
            const res = await sharedReportService.share(shareItem.snapshotId, {
              customerId, title: payload.title, recipients: payload.recipients, message: payload.message,
              expiryDays: payload.expiryDays, requirePassword: payload.requirePassword,
              password: payload.password, delivery: payload.delivery,
            })
            if (payload.delivery === 'copy') {
              try { await navigator.clipboard.writeText(res.url) } catch { /* clipboard blocked */ }
              showSuccess('Link created & copied', 'Paste it wherever you like.')
            } else {
              showSuccess('Report shared', `Emailed to ${payload.recipients.length} recipient${payload.recipients.length === 1 ? '' : 's'}.`)
            }
            setSharedReloadSignal((n) => n + 1)
          } catch (e) {
            showError('Could not share report', e instanceof Error ? e.message : 'Please try again.')
          }
        }}
      />
      <ReportViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        item={viewerItem}
        customerId={customerId}
        onChanged={() => setSharedReloadSignal((n) => n + 1)}
      />

      {/* Drawers */}
      {selectedReport && (
        <GenerateReportDrawer
          isOpen={isGenerateDrawerOpen}
          onClose={() => setIsGenerateDrawerOpen(false)}
          reportTitle={selectedReport.title}
          reportType={selectedReport.id}
          reportTypeDefinition={reportTypes.find(rt => rt.typeKey === selectedReport.id)}
          onGenerate={handleGenerateReport}
          onSaveShare={async (config) => {
            // Freeze a snapshot (POST /reports/save), then open the Share step with its id.
            try {
              const saved = await sharedReportService.save(config as unknown as Record<string, unknown>)
              const item: SharedReportListItem = {
                savedReportId: saved.savedReportId, snapshotId: saved.snapshotId, shareId: null,
                title: saved.title, reportType: config.reportType, reportTypeLabel: selectedReport.title,
                status: 'draft', dataAsOf: saved.dataAsOf, sentAt: null, expiresAt: null,
                viewCount: 0, recipients: [], hasPassword: false,
              }
              setIsGenerateDrawerOpen(false)
              setShareItem(item)
              setShareOpen(true)
              setSharedReloadSignal((n) => n + 1) // the saved (unshared) report now shows in history
            } catch (e) {
              showError('Could not save report', e instanceof Error ? e.message : 'Please try again.')
            }
          }}
        />
      )}

      {/* Schedule Drawer - supports both new schedules and editing */}
      <ScheduleReportDrawer
        isOpen={isScheduleDrawerOpen}
        onClose={handleCloseScheduleDrawer}
        reportTitle={
          editingSchedule?.reportTitle || selectedReport?.title || ''
        }
        reportTypeDefinition={reportTypes.find(
          rt => rt.typeKey === (editingSchedule?.reportType || selectedReport?.id)
        )}
        onSchedule={handleScheduleReport}
        editMode={!!editingSchedule}
        existingSchedule={editingSchedule}
        onUpdate={handleUpdateSchedule}
      />
    </div>
  )
}

export default ReportsPage
